// DEPLOY: /var/www/woody.wtf/frontend/src/chart/Navigator.js
// Desktop date slider under the chart: the whole history as a small close-price line, with
// the part on screen highlighted. Drag the highlight to move through time, drag either edge
// to zoom, click anywhere outside it to jump there. Redrawn whenever the chart view changes
// (ChartEngine onView -> draw()).
import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

const H = 46, EDGE = 6;

const Navigator = forwardRef(function Navigator({ engineRef }, ref) {
  const cv = useRef(null), drag = useRef(null);

  // Slider spans every candle plus the blank future slots up to the chart's right limit.
  const geo = () => {
    const eng = engineRef.current, c = cv.current;
    if (!eng || !c || !eng.bars.length) return null;
    const w = c.clientWidth - 64, n = eng.bars.length, total = Math.max(n - 1 + 30, eng.right + 1);
    const X = i => (i / total) * w, I = x => (x / w) * total;
    return { eng, w, n, X, I, left: Math.max(0, eng.idxAt(0)), right: eng.right };
  };

  const draw = () => {
    const c = cv.current, gm = geo();
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(c.clientWidth * dpr); c.height = Math.round(H * dpr);
    const g = c.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, c.clientWidth, H);
    if (!gm) return;
    const { eng, w, n, X, left, right } = gm, T = eng.theme, B = eng.bars;
    let lo = Infinity, hi = -Infinity;
    for (const b of B) { if (b.c < lo) lo = b.c; if (b.c > hi) hi = b.c; }
    const Y = v => 4 + (hi - v) / ((hi - lo) || 1) * (H - 8);
    g.beginPath();
    const step = Math.max(1, Math.floor(n / (w * 2)));
    for (let i = 0; i < n; i += step) { const x = X(i), y = Y(B[i].c); if (i) g.lineTo(x, y); else g.moveTo(x, y); }
    g.lineTo(X(n - 1), Y(B[n - 1].c));
    g.strokeStyle = T.muted; g.lineWidth = 1; g.stroke();
    g.lineTo(X(n - 1), H); g.lineTo(0, H); g.closePath();
    g.fillStyle = 'rgba(124,58,237,0.06)'; g.fill();
    const x1 = X(left), x2 = Math.min(w, X(right));
    g.fillStyle = 'rgba(124,58,237,0.14)'; g.fillRect(x1, 0, Math.max(2, x2 - x1), H);
    g.strokeStyle = T.accent; g.lineWidth = 1.5; g.strokeRect(x1 + 0.5, 0.5, Math.max(2, x2 - x1) - 1, H - 1);
    g.fillStyle = T.accent;
    for (const x of [x1, x2]) g.fillRect(x - 2, H / 2 - 8, 4, 16);
    g.strokeStyle = T.grid; g.beginPath(); g.moveTo(w + 0.5, 0); g.lineTo(w + 0.5, H); g.stroke();
  };

  useImperativeHandle(ref, () => ({ draw }));
  useEffect(() => { draw(); const ro = new ResizeObserver(draw); ro.observe(cv.current); return () => ro.disconnect(); }, []); // eslint-disable-line

  const pos = e => e.clientX - cv.current.getBoundingClientRect().left;

  const down = e => {
    const gm = geo();
    if (!gm) return;
    cv.current.setPointerCapture(e.pointerId);
    const x = pos(e), x1 = gm.X(gm.left), x2 = gm.X(gm.right);
    let kind = Math.abs(x - x1) <= EDGE ? 'left' : Math.abs(x - x2) <= EDGE ? 'right' : x > x1 && x < x2 ? 'move' : 'jump';
    if (kind === 'jump') {                         // centre the view where clicked, then drag it
      const half = (gm.right - gm.left) / 2;
      gm.eng.right = gm.I(x) + half; gm.eng.clampView(); gm.eng.draw();
      kind = 'move';
    }
    drag.current = { kind, x, left: Math.max(0, gm.eng.idxAt(0)), right: gm.eng.right };
  };

  const move = e => {
    const gm = geo(), d = drag.current, x = pos(e);
    if (gm) cv.current.style.cursor = drag.current ? cv.current.style.cursor
      : (Math.abs(x - gm.X(gm.left)) <= EDGE || Math.abs(x - gm.X(gm.right)) <= EDGE) ? 'ew-resize' : 'grab';
    if (!gm || !d) return;
    const eng = gm.eng, di = gm.I(x) - gm.I(d.x);
    if (d.kind === 'move') eng.right = d.right + di;
    else if (d.kind === 'left') eng.barW = eng.plotR / Math.max(5, d.right - (d.left + di));
    else { eng.right = Math.max(d.left + 5, d.right + di); eng.barW = eng.plotR / (eng.right - d.left); }
    eng.clampView(); eng.draw();
  };

  const up = () => { drag.current = null; };

  return <canvas ref={cv} style={{ display: 'block', width: '100%', height: H, flexShrink: 0, background: 'var(--surface)', borderTop: '1px solid var(--border)', touchAction: 'none' }}
                 onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} />;
});

export default Navigator;
